/**
 * Earnings Calculation Utilities
 *
 * Provides functions for calculating GST, financial summaries, and aggregating
 * earnings data across the hierarchy (Client → Job Site → Job → Instance)
 *
 * Billing Rules:
 * - Client Charge: min 1hr, then round up to nearest 1hr increments
 * - Contractor Payment: min 1hr, then round up to nearest 0.5hr increments
 * - Overtime: Only applies if job has estimated_hours and actual hours exceed it
 * - Overtime amounts only included if approved by corporate
 */

/**
 * Calculate client billable hours (1hr increments)
 * Rules: min 1 hour, then round up to nearest whole hour
 * @param {number} actualHours - Raw hours worked
 * @returns {number} Billable hours after rounding
 */
export function calculateClientBillableHours(actualHours) {
  if (!actualHours || actualHours <= 0) return 1;
  if (actualHours <= 1) return 1;
  return Math.ceil(actualHours);
}

/**
 * Calculate contractor billable hours (0.5hr increments)
 * Rules: min 1 hour, then round up to nearest 0.5 hour
 * @param {number} actualHours - Raw hours worked
 * @returns {number} Billable hours after rounding
 */
export function calculateContractorBillableHours(actualHours) {
  if (!actualHours || actualHours <= 0) return 1;
  if (actualHours <= 1) return 1;
  const afterFirst = actualHours - 1;
  return 1 + Math.ceil(afterFirst * 2) / 2;
}

/**
 * Calculate GST breakdown for a given amount
 * @param {number} amount - The monetary amount
 * @param {boolean} includeGst - Whether GST applies
 * @param {string} gstType - 'inclusive' or 'exclusive'
 * @param {number} gstRate - GST percentage (e.g., 10 for 10%)
 * @returns {{ baseAmount: number, gstAmount: number, totalAmount: number } | null}
 */
export function calculateGstBreakdown(amount, includeGst, gstType, gstRate = 10) {
  if (!includeGst || amount === null || amount === undefined) {
    return null;
  }

  const parsedAmount = parseFloat(amount) || 0;
  const rate = parseFloat(gstRate) / 100;

  if (gstType === 'inclusive') {
    // Amount includes GST - work backwards
    const baseAmount = parsedAmount / (1 + rate);
    const gstAmount = parsedAmount - baseAmount;
    return {
      baseAmount: Math.round(baseAmount * 100) / 100,
      gstAmount: Math.round(gstAmount * 100) / 100,
      totalAmount: parsedAmount
    };
  } else {
    // Amount is exclusive - add GST
    const gstAmount = parsedAmount * rate;
    return {
      baseAmount: parsedAmount,
      gstAmount: Math.round(gstAmount * 100) / 100,
      totalAmount: Math.round((parsedAmount + gstAmount) * 100) / 100
    };
  }
}

/**
 * Calculate financial summary for a single job instance
 * @param {Object} instance - The job instance with timesheet and overtime_request data
 * @param {Object} job - The job with payment and GST settings
 * @param {number} companyGstRate - Company's default GST rate
 * @returns {Object} Financial breakdown including base and overtime amounts
 */
export function calculateInstanceFinancials(instance, job, companyGstRate = 10) {
  const timesheet = instance?.timesheet?.[0] || instance?.timesheet;
  const overtimeRequest = instance?.overtime_request?.[0] || instance?.overtime_request;
  const actualHours = parseFloat(timesheet?.total_hours) || 0;
  const estimatedHours = parseFloat(job?.estimated_hours) || null;

  // Calculate billable hours (different for client vs contractor)
  const clientBillableHours = calculateClientBillableHours(actualHours);
  const contractorBillableHours = parseFloat(timesheet?.capped_hours) || calculateContractorBillableHours(actualHours);

  // Check for overtime (only if estimated_hours exists and exceeded)
  const hasOvertime = estimatedHours && clientBillableHours > estimatedHours;
  const clientOvertimeHours = hasOvertime ? clientBillableHours - estimatedHours : 0;
  const contractorOvertimeHours = hasOvertime ? Math.max(0, contractorBillableHours - estimatedHours) : 0;

  // Check if overtime is APPROVED by corporate
  const overtimeApproved = overtimeRequest?.status === 'approved' || overtimeRequest?.status === 'partially_approved';
  const overtimePending = overtimeRequest?.status === 'pending';

  // Base hours (capped at estimated if overtime exists)
  const clientBaseHours = hasOvertime ? estimatedHours : clientBillableHours;
  const contractorBaseHours = hasOvertime ? Math.min(contractorBillableHours, estimatedHours) : contractorBillableHours;

  // Get charge type and amount
  const chargeType = job?.client_charge_type || 'hourly';
  const clientChargeAmount = parseFloat(job?.client_charge_amount) || 0;
  const chargeHasGst = job?.client_include_gst ?? true;

  // Get payment type and amount
  const paymentType = job?.payment_type || 'hourly';
  const paymentAmount = parseFloat(job?.payment_amount) || 0;
  const payoutHasGst = job?.contractor_include_gst ?? false;

  // ===== CHECK FOR CHARGE ADJUSTMENT (override) =====
  const hasChargeAdjustment = instance?.charge_adjustment !== null && instance?.charge_adjustment !== undefined;
  const chargeAdjustmentAmount = hasChargeAdjustment ? parseFloat(instance.charge_adjustment) : null;
  const chargeAdjustmentReason = instance?.charge_adjustment_reason || null;

  // ===== ADDITIONAL CHARGES =====
  const additionalCharges = instance?.additional_charges || [];
  let totalAdditionalCharges = 0;
  let totalAdditionalGst = 0;

  additionalCharges.forEach(charge => {
    totalAdditionalCharges += parseFloat(charge.amount) || 0;
    if (charge.include_gst) {
      totalAdditionalGst += parseFloat(charge.gst_amount) || 0;
    }
  });

  // ===== BASE CLIENT CHARGE =====
  let chargeToClient = null;
  let gstOnCharge = null;

  // If there's a charge adjustment, use that instead of calculating
  if (hasChargeAdjustment) {
    // Adjustment is the final amount - recalculate GST based on job settings
    if (chargeHasGst && chargeAdjustmentAmount > 0) {
      const gstBreakdown = calculateGstBreakdown(
        chargeAdjustmentAmount,
        true,
        job?.client_gst_type || 'exclusive',
        companyGstRate
      );
      if (gstBreakdown) {
        chargeToClient = gstBreakdown.totalAmount;
        gstOnCharge = gstBreakdown.gstAmount;
      }
    } else {
      chargeToClient = chargeAdjustmentAmount;
      gstOnCharge = 0;
    }
  } else if (job?.client_charge_amount !== null && job?.client_charge_amount !== undefined) {
    // For hourly, multiply by base hours; for fixed, use the amount directly
    const rawBaseCharge = chargeType === 'hourly' ? clientChargeAmount * clientBaseHours : clientChargeAmount;

    if (chargeHasGst && rawBaseCharge > 0) {
      const gstBreakdown = calculateGstBreakdown(
        rawBaseCharge,
        true,
        job?.client_gst_type || 'exclusive',
        companyGstRate
      );
      if (gstBreakdown) {
        chargeToClient = gstBreakdown.totalAmount;
        gstOnCharge = gstBreakdown.gstAmount;
      }
    } else {
      chargeToClient = rawBaseCharge;
      gstOnCharge = 0;
    }
  }

  // ===== OVERTIME CLIENT CHARGE (only if approved) =====
  let overtimeChargeToClient = null;
  let gstOnOvertimeCharge = null;

  if (hasOvertime && overtimeApproved && chargeType === 'hourly' && clientChargeAmount > 0) {
    const rawOvertimeCharge = clientChargeAmount * clientOvertimeHours;

    if (chargeHasGst && rawOvertimeCharge > 0) {
      const gstBreakdown = calculateGstBreakdown(
        rawOvertimeCharge,
        true,
        job?.client_gst_type || 'exclusive',
        companyGstRate
      );
      if (gstBreakdown) {
        overtimeChargeToClient = gstBreakdown.totalAmount;
        gstOnOvertimeCharge = gstBreakdown.gstAmount;
      }
    } else {
      overtimeChargeToClient = rawOvertimeCharge;
      gstOnOvertimeCharge = 0;
    }
  }

  // ===== BASE CONTRACTOR PAYOUT =====
  // Use timesheet.base_amount if available, otherwise calculate
  let payoutToContractor = parseFloat(timesheet?.base_amount) || 0;

  // If no base_amount, calculate from job payment settings
  if (payoutToContractor === 0 && paymentAmount > 0) {
    payoutToContractor = paymentType === 'hourly' ? paymentAmount * contractorBaseHours : paymentAmount;
  }

  // If still 0 but we have amount_earned and no overtime, use amount_earned
  if (payoutToContractor === 0 && !hasOvertime) {
    payoutToContractor = parseFloat(timesheet?.amount_earned) || 0;
  }

  let gstOnPayout = null;

  if (payoutHasGst && payoutToContractor > 0) {
    const gstBreakdown = calculateGstBreakdown(
      payoutToContractor,
      true,
      job?.contractor_gst_type || 'exclusive',
      companyGstRate
    );
    if (gstBreakdown) {
      payoutToContractor = gstBreakdown.totalAmount;
      gstOnPayout = gstBreakdown.gstAmount;
    }
  } else {
    gstOnPayout = 0;
  }

  // ===== OVERTIME CONTRACTOR PAYOUT (only if approved) =====
  let overtimePayoutToContractor = null;
  let gstOnOvertimePayout = null;

  if (hasOvertime && overtimeApproved) {
    // Use approved overtime amount from timesheet or request
    overtimePayoutToContractor = parseFloat(timesheet?.overtime_amount) ||
      parseFloat(overtimeRequest?.approved_overtime_amount) || 0;

    if (payoutHasGst && overtimePayoutToContractor > 0) {
      const gstBreakdown = calculateGstBreakdown(
        overtimePayoutToContractor,
        true,
        job?.contractor_gst_type || 'exclusive',
        companyGstRate
      );
      if (gstBreakdown) {
        overtimePayoutToContractor = gstBreakdown.totalAmount;
        gstOnOvertimePayout = gstBreakdown.gstAmount;
      }
    } else {
      gstOnOvertimePayout = 0;
    }
  }

  // ===== COMMISSION =====
  // Note: Commission was previously calculated for partner_agency jobs with commission_rate.
  // As part of the Clients + Job Sites restructure, commission tracking has been removed.
  // This field is kept for backward compatibility but will always be null for new jobs.
  let commissionMargin = null;

  // Legacy support: Check for old partner_agency commission data
  // This maintains backward compatibility for historical data
  if (job?.partner_agency?.commission_rate && chargeToClient !== null) {
    const commissionRate = parseFloat(job.partner_agency.commission_rate) || 0;
    // Calculate commission on base charge amount (excluding GST)
    const baseChargeForCommission = chargeToClient - (gstOnCharge || 0);
    commissionMargin = Math.round((baseChargeForCommission * commissionRate / 100) * 100) / 100;
  }

  // ===== PROFIT CALCULATION =====
  // Base Profit = (Charge - GST) - Commission - (Payout - GST on Payout)
  let profit = null;
  if (chargeToClient !== null) {
    const baseChargeNet = chargeToClient - (gstOnCharge || 0);
    const basePayoutNet = payoutToContractor - (gstOnPayout || 0);
    profit = baseChargeNet - (commissionMargin || 0) - basePayoutNet;
    profit = Math.round(profit * 100) / 100;
  }

  // Overtime Profit (only if approved)
  let overtimeProfit = null;
  if (hasOvertime && overtimeApproved && overtimeChargeToClient !== null) {
    const overtimeChargeNet = overtimeChargeToClient - (gstOnOvertimeCharge || 0);
    const overtimePayoutNet = (overtimePayoutToContractor || 0) - (gstOnOvertimePayout || 0);
    overtimeProfit = overtimeChargeNet - overtimePayoutNet;
    overtimeProfit = Math.round(overtimeProfit * 100) / 100;
  }

  return {
    // Base amounts
    chargeToClient,
    gstOnCharge,
    chargeHasGst,
    payoutToContractor,
    gstOnPayout,
    payoutHasGst,
    commissionMargin,
    profit,

    // Overtime amounts (only populated if approved)
    overtimeChargeToClient,
    gstOnOvertimeCharge,
    overtimePayoutToContractor,
    gstOnOvertimePayout,
    overtimeProfit,

    // Overtime metadata
    hasOvertime,
    overtimeApproved,
    overtimePending,
    clientOvertimeHours,
    contractorOvertimeHours,
    estimatedHours,

    // Hours info
    actualHours,
    clientBillableHours,
    contractorBillableHours,
    clientBaseHours,
    contractorBaseHours
  };
}

/**
 * Aggregate financial totals from an array of financial objects
 * @param {Array} financials - Array of financial objects from calculateInstanceFinancials
 * @returns {Object} Aggregated totals
 */
export function aggregateFinancials(financials) {
  const result = {
    // Base totals
    chargeToClient: 0,
    gstOnCharge: 0,
    commissionMargin: 0,
    payoutToContractor: 0,
    gstOnPayout: 0,
    profit: 0,

    // Overtime totals
    overtimeChargeToClient: 0,
    gstOnOvertimeCharge: 0,
    overtimePayoutToContractor: 0,
    gstOnOvertimePayout: 0,
    overtimeProfit: 0,

    // Flags
    hasAnyCharge: false,
    hasAnyCommission: false,
    hasAnyOvertime: false,
    hasAnyOvertimeApproved: false,
    hasAnyOvertimePending: false
  };

  financials.forEach(f => {
    // Base amounts
    if (f.chargeToClient !== null) {
      result.chargeToClient += f.chargeToClient;
      result.hasAnyCharge = true;
    }
    if (f.gstOnCharge !== null) {
      result.gstOnCharge += f.gstOnCharge;
    }
    if (f.commissionMargin !== null) {
      result.commissionMargin += f.commissionMargin;
      result.hasAnyCommission = true;
    }
    result.payoutToContractor += f.payoutToContractor || 0;
    if (f.gstOnPayout !== null) {
      result.gstOnPayout += f.gstOnPayout;
    }
    if (f.profit !== null) {
      result.profit += f.profit;
    }

    // Overtime amounts
    if (f.hasOvertime) {
      result.hasAnyOvertime = true;
      if (f.overtimeApproved) {
        result.hasAnyOvertimeApproved = true;
      }
      if (f.overtimePending) {
        result.hasAnyOvertimePending = true;
      }
    }
    if (f.overtimeChargeToClient !== null) {
      result.overtimeChargeToClient += f.overtimeChargeToClient;
    }
    if (f.gstOnOvertimeCharge !== null) {
      result.gstOnOvertimeCharge += f.gstOnOvertimeCharge;
    }
    if (f.overtimePayoutToContractor !== null) {
      result.overtimePayoutToContractor += f.overtimePayoutToContractor;
    }
    if (f.gstOnOvertimePayout !== null) {
      result.gstOnOvertimePayout += f.gstOnOvertimePayout;
    }
    if (f.overtimeProfit !== null) {
      result.overtimeProfit += f.overtimeProfit;
    }
  });

  // Round all values
  result.chargeToClient = Math.round(result.chargeToClient * 100) / 100;
  result.gstOnCharge = Math.round(result.gstOnCharge * 100) / 100;
  result.commissionMargin = Math.round(result.commissionMargin * 100) / 100;
  result.payoutToContractor = Math.round(result.payoutToContractor * 100) / 100;
  result.gstOnPayout = Math.round(result.gstOnPayout * 100) / 100;
  result.profit = Math.round(result.profit * 100) / 100;
  result.overtimeChargeToClient = Math.round(result.overtimeChargeToClient * 100) / 100;
  result.gstOnOvertimeCharge = Math.round(result.gstOnOvertimeCharge * 100) / 100;
  result.overtimePayoutToContractor = Math.round(result.overtimePayoutToContractor * 100) / 100;
  result.gstOnOvertimePayout = Math.round(result.gstOnOvertimePayout * 100) / 100;
  result.overtimeProfit = Math.round(result.overtimeProfit * 100) / 100;

  return result;
}

/**
 * Format currency value for display
 * @param {number|null} value - The value to format
 * @param {boolean} showNA - Show 'N/A' for null values (default: true)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value, showNA = true) {
  if (value === null || value === undefined) {
    return showNA ? 'N/A' : '-';
  }
  return `$${parseFloat(value).toFixed(2)}`;
}

/**
 * Format GST value for display
 * @param {number|null} value - The GST amount
 * @param {boolean} hasGst - Whether GST applies
 * @returns {string} Formatted GST string
 */
export function formatGst(value, hasGst) {
  if (!hasGst) {
    return 'No GST';
  }
  if (value === null || value === undefined || value === 0) {
    return '$0.00';
  }
  return `$${parseFloat(value).toFixed(2)}`;
}

/**
 * Format overtime value for display
 * @param {number|null} value - The overtime amount
 * @param {number} hours - The overtime hours
 * @param {boolean} approved - Whether overtime is approved
 * @param {boolean} pending - Whether overtime is pending
 * @returns {string} Formatted overtime string
 */
export function formatOvertime(value, hours, approved, pending) {
  if (!approved && !pending) {
    return '-';
  }
  if (pending) {
    return 'Pending';
  }
  if (value === null || value === undefined || value === 0) {
    return '-';
  }
  return `$${parseFloat(value).toFixed(2)} (${hours}hr)`;
}

/**
 * Format hours for display
 * @param {number} decimalHours - Hours in decimal format
 * @returns {string} Formatted as "Xhr Ymn"
 */
export function formatHoursMinutes(decimalHours) {
  if (!decimalHours || decimalHours === 0) return '0hr 0mn';
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours}hr ${minutes}mn`;
}
